/**
 * „Alles neu übertragen" — vollstaendige Neuuebertragung ausgewaehlter Apartments.
 *
 * Einen ganzen Bestand ungefragt neu zu senden ergibt selten Sinn; der Dialog laesst deshalb
 * bewusst auswaehlen. Vorbelegt ist hoechstens das Apartment, an dem gerade gearbeitet wird.
 * Channex kennt den Full-Sync nur je Apartment: jeder Lauf ist ein eigener 202-Auftrag in die
 * bestehende Outbox, nacheinander — es entsteht kein Sammelschreibpfad. Vor dem Senden steht ein
 * Bestätigungsschritt, der sagt, was passiert; erst dessen Knopf löst eine Anfrage aus.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import {
  problemText,
  toApiError,
  useApi,
  type AvailabilityDistributionApartment,
} from '@/shared/api';
import { addTrackedAriOperations } from '@/features/properties/ari-edit';
import { ApartmentChecklist } from './ApartmentChecklist';

interface FullResyncDialogProps {
  apartments: AvailabilityDistributionApartment[];
  preselectedPropertyId: string | null;
  onClose: () => void;
  onFinished: () => void;
}

export function FullResyncDialog({
  apartments,
  preselectedPropertyId,
  onClose,
  onFinished,
}: FullResyncDialogProps) {
  const api = useApi();
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(preselectedPropertyId ? [preselectedPropertyId] : []),
  );
  const [done, setDone] = useState(0);
  const [running, setRunning] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const chosen = apartments.filter((apartment) => selected.has(apartment.propertyId));

  async function run() {
    setRunning(true);
    const failed: string[] = [];
    for (const apartment of chosen) {
      try {
        const result = await api.channex.fullSync(apartment.propertyId);
        addTrackedAriOperations(result.propertyId, result.operationIds);
      } catch (error) {
        const problem = await toApiError(error);
        failed.push(`${apartment.name}: ${problemText(problem, 'nicht vorgemerkt.')}`);
      }
      setDone((current) => current + 1);
    }
    setRunning(false);
    onFinished();
    onClose();
    if (failed.length === 0) {
      toast.success(`${countLabel(chosen.length)} zur Neuübertragung vorgemerkt.`);
    } else {
      toast.error(`${failed.length} von ${chosen.length} fehlgeschlagen.`, {
        description: failed.join(' · '),
        duration: 15000,
      });
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && !running && onClose()}>
      <DialogContent className="max-w-lg" showClose={!running}>
        <DialogHeader>
          <DialogTitle>Neu übertragen</DialogTitle>
          <DialogDescription>
            Wähle die Apartments, deren Preise und Verfügbarkeit vollständig neu an Channex
            übertragen werden. Jedes Apartment läuft einzeln nacheinander.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {confirming ? (
            <p className="text-ink text-sm leading-relaxed">
              Sendet Preise, Sperren und Restriktionen von{' '}
              <b>{chosen.map((apartment) => apartment.name).join(', ')}</b> erneut an Channex. Nur
              nötig, wenn Kanäle einen falschen Stand zeigen; die Werte in StayOn bleiben, wie sie
              sind.
            </p>
          ) : (
            <ApartmentChecklist
              apartments={apartments}
              selected={selected}
              disabled={running}
              onChange={setSelected}
            />
          )}
          {running && (
            <p className="text-ink-soft mt-3 text-[13px]" aria-live="polite">
              {done} von {chosen.length} übertragen…
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={confirming ? () => setConfirming(false) : onClose}
            disabled={running}
          >
            {confirming ? 'Zurück' : 'Abbrechen'}
          </Button>
          {confirming ? (
            <Button variant="brand" onClick={() => void run()} loading={running}>
              Jetzt neu übertragen
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => setConfirming(true)}
              disabled={chosen.length === 0}
            >
              {chosen.length === 0
                ? 'Apartments wählen'
                : `${countLabel(chosen.length)} übertragen …`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function countLabel(count: number): string {
  return count === 1 ? '1 Apartment' : `${count} Apartments`;
}
